/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import * as pageSelectors from "selectors/pageSelectors";
import { getMargin } from "selectors/zoom";
import { getHideStickyToolOnTap } from "selectors/appSelectors";
import { shouldRenderPageShadows } from "selectors/appUiStateSelectors";
import { PageView } from "components/document/page/PageView";
import { PageCanvasContainer } from "containers/document/page/PageCanvasContainer";
import { AccessiblePageContentContainer } from "containers/accessibility/AccessiblePageContentContainer";
import { PAGE_MARGIN_BOTTOM, PROGRESSIVE_RENDERING_WORKFLOWS } from "constants/constants";
import { getViewPortWidthWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import {
  isSinglePageMode,
  getViewRotationAngle,
  isTwoColumnViewMode,
  isFullScreenOrFitOnePageMode,
  getIsPageProgressivelyRendered,
  getUseProgressiveRendering
} from "selectors/documentSelectors";
import { shouldFetchAccContent, getPageIndexToFocus } from "selectors/accessibilitySelectors";
import { loadStatus } from "constants/enums";
import { getProgressiveCanvasId } from "utils/PageUtils";
import docApi from "api";
import { recordTimeToViewFirstPage } from "utils/performance";
import { isDarkPageThemeEnabled } from "selectors/appSelectors";
import { withTheme } from "context/themeContext";

const pageOverlays = [PageCanvasContainer];

function mapStoreToProps(stores, ownProps) {
  return {
    pageBitmap: pageSelectors.getPageBitmap(stores, ownProps.pageIndex),
    height: pageSelectors.getPageHeight(stores, ownProps.pageIndex),
    width: pageSelectors.getPageWidth(stores, ownProps.pageIndex),
    pageIndex: ownProps.pageIndex,
    viewRotationAngle: getViewRotationAngle(stores),
    overlays: pageOverlays,
    pageMarginBottom: !(isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) ? PAGE_MARGIN_BOTTOM : 0,
    margins: getMargin(stores),
    setPageHandle: pageHandle => {
      stores.documentStore.setPageHandle(pageHandle, ownProps.pageIndex);
    },
    shouldRenderPageShadows: shouldRenderPageShadows(stores),
    PageAccessibilityComponent: AccessiblePageContentContainer,
    pageTilesCount: (() => {
      const pageTiles = pageSelectors.getPageTiles(stores, ownProps.pageIndex);
      return pageTiles ? pageTiles.length : 0;
    })(),
    viewportWidth: getViewPortWidthWithoutPagesViewScrollbar(stores),
    isTwoColumn: isTwoColumnViewMode(stores),
    onMount: () => stores.documentStore.setPageMountStatus(ownProps.pageIndex, true),
    onUnMount: () => {
      stores.documentStore.setPageMountStatus(ownProps.pageIndex, false);
      if (getUseProgressiveRendering(stores)) {
        stores.documentStore.setProgressivelyRenderedPageStatus(ownProps.pageIndex, loadStatus.UNLOADED);
        const canvasId = getProgressiveCanvasId(ownProps.pageIndex, PROGRESSIVE_RENDERING_WORKFLOWS.PAGE_RENDERING);
        docApi.removeOffScreenCanvas(canvasId);
      }
    },
    isLeftPageIndex: index => pageSelectors.isLeftPageIndex(stores, index),
    /* Pass onmousemove, onclick etc events here if there is no overlay */
    enableAccessibilityFixes: shouldFetchAccContent(stores),
    refCallback: elementHandle => {
      if (getPageIndexToFocus(stores) === ownProps.pageIndex && elementHandle) {
        elementHandle.focus();
        stores.accessibilityStore.setPageIndexToFocus(-1);
      }
    },
    useSinglePageCSS: ownProps.useSinglePageCSS,
    isProgressivePage: getIsPageProgressivelyRendered(stores, ownProps.pageIndex),
    isPageLoaded: pageSelectors.getPageLoadStatus(stores, ownProps.pageIndex) === loadStatus.LOADED,
    itemType: "page",
    isProgressiveRenderingEnabled: getUseProgressiveRendering(stores),
    onFirstPageRendered: () => {
      recordTimeToViewFirstPage();
      stores.pagedViewState.setIsInitialPageRendered(true);
    },
    hideStickyToolOnTap: getHideStickyToolOnTap(stores),
    toggleStickyToolPanel: () => stores.stickyToolsStore.toggleStickyToolPanel(),
    invertColors: isDarkPageThemeEnabled(stores)
  };
}

const ClassicPDFPageContainer = inject(mapStoreToProps)(withTheme(PageView));

export { ClassicPDFPageContainer };
